import { FilterGroup, FilterCondition } from 'types/userConfig/userConfig';
import { mapFieldToApiField } from './field-mapping';
import { resolveDynamicValue } from './resolve-dynamic-values';

/**
 * ממיר FilterGroup[] למבנה API שצפוי על ידי השרת
 */
export function convertFilterGroupsToApiFilters(filterGroups: FilterGroup[]): any[] {
  if (!filterGroups || filterGroups.length === 0) {
    return [];
  }

  return filterGroups.map((group) => {
    const conditions = group.conditions
      .filter((cond) => cond.field && cond.operator) // רק תנאים תקינים
      .map((cond) => {
        // המרת שדה לשדה API
        const apiField = mapFieldToApiField(cond.field);
        
        // טיפול בשדות From/To - המרת operator לפי הסיומת
        let finalOperator = cond.operator;
        let finalField = apiField;
        
        // אם השדה מסתיים ב-From, נשתמש ב-gte (greater than or equal) או gt
        // אם השדה מסתיים ב-To, נשתמש ב-lte (less than or equal) או lt
        if (cond.field.endsWith('From')) {
          // עבור From, נשתמש ב-gte אם operator הוא eq, אחרת gt
          finalOperator = cond.operator === 'eq' ? 'gte' : 'gt';
        } else if (cond.field.endsWith('To')) {
          // עבור To, נשתמש ב-lte אם operator הוא eq, אחרת lt
          finalOperator = cond.operator === 'eq' ? 'lte' : 'lt';
        }
        
        const apiCondition: any = {
          field: finalField,
          operator: finalOperator,
        };

        // הוסף value רק אם הוא נדרש (לא עבור isnull/isnotnull)
        if (finalOperator !== 'isnull' && finalOperator !== 'isnotnull') {
          if (cond.value !== undefined && cond.value !== null) {
            // קבע את סוג השדה להמרת ערכים דינמיים
            let fieldType: 'dateTime' | 'manager' | 'other' = 'other';
            if (finalField.includes('date') || finalField.includes('Date') || finalField.includes('Time')) {
              fieldType = 'dateTime';
            } else if (finalField === 'managerId') {
              fieldType = 'manager';
            }
            
            // המר ערכים דינמיים לערכים אמיתיים
            let resolvedValue = cond.value;
            if (Array.isArray(cond.value)) {
              resolvedValue = cond.value.map(v => resolveDynamicValue(v, fieldType));
            } else {
              resolvedValue = resolveDynamicValue(cond.value, fieldType);
            }
            
            // עבור operators 'eq' ו-'ne', הערך צריך להיות string (לא array)
            // עבור operators 'in' ו-'notin', הערך צריך להיות array
            if ((finalOperator === 'eq' || finalOperator === 'ne') && Array.isArray(resolvedValue)) {
              // אם זה array אבל operator הוא eq/ne, קח את האלמנט הראשון
              apiCondition.value = resolvedValue.length > 0 ? resolvedValue[0] : null;
            } else if ((finalOperator === 'in' || finalOperator === 'notin') && !Array.isArray(resolvedValue)) {
              // אם זה לא array אבל operator הוא in/notin, המר ל-array
              apiCondition.value = [resolvedValue];
            } else {
              apiCondition.value = resolvedValue;
            }
          }
        }

        return apiCondition;
      });

    // אם אין תנאים תקינים, החזר null (ייסונן אחר כך)
    if (conditions.length === 0) {
      return null;
    }

    const result: any = {
      conditions,
    };

    // הוסף logic רק אם זה 'or' (ברירת מחדל היא 'and')
    if (group.logic === 'or') {
      result.logic = 'or';
    }

    return result;
  }).filter((filter) => filter !== null); // הסר nulls
}

/**
 * ממיר את הלוגיקה בין קבוצות למבנה API
 * אם filtersLogic הוא 'or', צריך לשלוח את כל הקבוצות עם logic: 'or' ביניהן
 * אם filtersLogic הוא 'and' (ברירת מחדל), כל קבוצה נשלחת בנפרד
 */
export function convertCustomFiltersToApiFilters(
  filterGroups: FilterGroup[],
  filtersLogic?: 'or' | 'and',
): any[] {
  const apiFilters = convertFilterGroupsToApiFilters(filterGroups);

  if (apiFilters.length === 0) {
    return [];
  }

  // אם יש רק קבוצה אחת או logic הוא 'and', החזר את כל הקבוצות בנפרד
  if (apiFilters.length === 1 || filtersLogic === 'and' || !filtersLogic) {
    return apiFilters;
  }

  // אם logic הוא 'or', צריך לשלוח את כל הקבוצות עם logic: 'or' ביניהן
  // אבל לפי המבנה של השרת, כל קבוצה היא filter נפרד, אז נשלח אותן בנפרד
  // השרת יבין שהן OR לפי הסדר או לפי מבנה אחר
  // כרגע נשלח אותן בנפרד - השרת צריך לטפל ב-logic בין filters
  
  // אם השרת מצפה למבנה עם logic ברמה העליונה, צריך לשלוח כך:
  // { logic: 'or', conditions: [...] } - אבל זה לא תואם למבנה הנוכחי
  
  // כרגע נשלח כל קבוצה בנפרד - השרת צריך לטפל ב-logic בין filters
  return apiFilters;
}

